import { ChangeEvent, FormEvent, useState } from "react";
import {
  RadioOption,
  TableTitle,
} from "../../components/mobileSize/TableTitle";
import { Box } from "@mui/material";
import { SearchForm } from "../../components/common/SearchForm";
import { Button } from "../../components/common/Button";
import theme from "../../theme";
import {
  CustomListType,
  Customer,
  SystemSetting,
} from "../../types/types";
import { DotLoader } from "../../components/dotLoader";
import styled from "@emotion/styled";
import Spinner from "../../components/spinner";
import { MobileTable } from "../../components/mobileSize/MobileTable";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { DetailUserIcon, TrashCanIcon } from "../../assets/icons/icons";
import { PhoneMaskToggle } from "../../components/PhoneMaskToggle";
import userStorage from "../../utils/userStorage";
import { SelectBoxGroupId } from "../../components/SelectBoxGroupId";
import { FetchParams } from "./manageCustomer";
import { useI18n } from "../../locales";

type Prop = {
  data?: CustomListType<Customer[]>;
  isLoading: boolean;
  systemSetting: SystemSetting | null;
  onClickBtn: ({ searchKey, groupId }: FetchParams) => void;
  sort?: string;
  setSort?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setOpenRegisterModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClickDetailIcon: (el: Customer) => void;
  onClickDeleteIcon: (el: Customer) => void;
};

export const MobileManageCustomer = ({
  data,
  isLoading,
  systemSetting,
  onClickBtn,
  sort,
  setSort,
  setOpenRegisterModal,
  onClickDetailIcon,
  onClickDeleteIcon,
}: Prop) => {
  const [openFilter, setOpenFilter] = useState(false);
  const [input, setInput] = useState("");
  const user = userStorage.get();
  const [groupSub, setGroupSub] = useState("all"); // 하위그룹코드(화면용)
  const { t } = useI18n();

  const SORT_LIST: RadioOption[] = [
    { label: t.manageCustomer.defaultSort, value: `` },
    { label: t.manageCustomer.sortByNumber, value: `seq` },
    { label: t.manageCustomer.sortByContact, value: `id` },
    { label: t.manageCustomer.sortByName, value: `name` },
    { label: t.manageCustomer.sortByAddress, value: `address` },
    { label: t.manageCustomer.sortByDetailAddress, value: `address_detail` },
    { label: t.manageCustomer.sortByGroup, value: `group_name` },
  ];

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.currentTarget.value);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onClickBtn({
      searchKey: input,
      groupId: groupSub,
    });
  };

  return (
    <>
      {/* 필터 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: openFilter ? "auto" : "0px",
          maxHeight: openFilter ? "100vh" : "0px",
          overflow: openFilter ? "visible" : "hidden",
          gap: "6px",
          paddingBottom: openFilter ? "16px" : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `8px`,
            alignItems: "center",
          }}
        >
          {/* 하위 그룹 조회 */}
          <SelectBoxGroupId
            groupSub={groupSub}
            setGroupSub={setGroupSub}
            width={140}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: `5px`,
            alignItems: "center",
          }}
        >
          <SearchForm
            onSubmit={onSubmit}
            placeholder={t.common.search}
            onChange={onChange}
            inputValue={input}
            setInputValue={setInput}
            width="140px"
            gap="5px"
            bottom="0"
            marginR="0"
          />
          <Button
            bgColor={theme.color.aquamarine_500}
            hoverColor={theme.color.aquamarine_600}
            height="30px"
            isDisabled={!systemSetting?.create}
            width="95px"
            onClick={() => setOpenRegisterModal(true)}
            paddingX="0px"
          >
            {t.manageCustomer.registerOwner}
          </Button>
        </Box>
      </Box>

      {/* 테이블 타이틀 섹션 */}
      <TableTitle
        title={
          isLoading ? (
            <div style={{ height: "15px" }}>
              <DotLoader height="6px" />
            </div>
          ) : (
            `${t.manageCustomer.ownerInfo} - ${data?.total_items ?? 0} ${t.manageCustomer.casesUnit}`
          )
        }
        sort={sort}
        setSort={setSort}
        setOpenFilter={setOpenFilter}
        sortList={SORT_LIST}
      />

      {/* 테이블 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          flexGrow: 1,
        }}
      >
        {isLoading && <Spinner />}
        {data &&
          data.list.length > 0 &&
          data.list.map((el) => (
            <MobileTable
              key={el.seq.toString()}
              defaultInfoGap="0px"
              defaultInfo={
                <>
                  <TextWrapper>
                    {/* 소유자번호 */}
                    <Text $width="16%">{el.seq}</Text>
                    {/* 아이디 - 연락처 */}
                    <Text $width="39%">
                      {/* {el.id} */}
                      <PhoneMaskToggle
                        text={el.id}
                        who={1} // 소유자
                        userIdx={user?.idx}
                        name={el.name}
                        isMobile
                      />
                    </Text>
                    {/* 이름 */}
                    <CustomTooltip title={el?.name} active>
                      <Text $width="43%" style={{ textAlign: "right" }}>
                        {el.name}
                      </Text>
                    </CustomTooltip>
                  </TextWrapper>

                  <TextWrapper>
                    {/* 주소 */}
                    <CustomTooltip
                      title={el?.address}
                      active={!!el.address}
                    >
                      <Text>{el.address ? el.address : t.manageCustomer.noAddress}</Text>
                    </CustomTooltip>
                  </TextWrapper>

                  <TextWrapper>
                    {/* 담당기관 (그룹) */}
                    <CustomTooltip
                      title={el.group_name}
                      active={!!el.group_name}
                    >
                      <Text>
                        {/* {el.group_name ? el.group_name : "담당기관 없음"} */}
                        {el.group_name ? el.group_name : t.manageCustomer.noGroup}
                      </Text>
                    </CustomTooltip>
                    {/* 생년월일 */}
                    {/* <Text $width="16%" style={{ textAlign: "center" }}>
                      {el.birth ? el.birth : "-"}
                    </Text> */}
                  </TextWrapper>
                </>
              }
              detailInfo={
                <DetailInfoWrapper>
                  <Table>
                    <tbody>
                      {/* 상세주소*/}
                      <DetailTextWrapper>
                        <DetailTitle>{t.manageCustomer.detailAddressLabel}</DetailTitle>
                        <CustomTooltip
                          title={el?.address_detail}
                          active={!!el.address_detail}
                        >
                          <DetailText>
                            {el.address_detail
                              ? el.address_detail
                              : t.manageCustomer.noDetailAddress}
                          </DetailText>
                        </CustomTooltip>
                      </DetailTextWrapper>
                    </tbody>
                  </Table>
                  <IconsWrapper>
                    {/* 정보 상세 보기 */}
                    <IconWrapper onClick={() => onClickDetailIcon(el)}>
                      <DetailUserIcon />
                    </IconWrapper>

                    {/* 계정 삭제하기 */}
                    <IconWrapper
                      onClick={() => {
                        if (!systemSetting?.delete) return;
                        onClickDeleteIcon(el);
                      }}
                    >
                      <TrashCanIcon />
                    </IconWrapper>
                  </IconsWrapper>
                </DetailInfoWrapper>
              }
            />
          ))}
        {!isLoading && data && data.list.length === 0 && (
          <NoDataMassage>{t.manageCustomer.noDataFound}</NoDataMassage>
        )}
      </Box>
    </>
  );
};

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Text = styled.div<{ $fontSize?: string; $width?: string }>`
  flex: ${({ $width }) => ($width ? `0 1 ${$width}` : "1")};
  width: ${({ $width }) => ($width ? $width : "calc(100% - 32px)")};
  max-width: ${({ $width }) =>
    $width ? $width : "calc(100vw- 32px- 32px)"};
  font-size: ${({ $fontSize }) =>
    $fontSize ? $fontSize : theme.fontSize.FONT_2};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailInfoWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Table = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;

  td {
    /* border: 0.5px solid ${theme.color.neutral_100}; */
  }
`;

const DetailTextWrapper = styled.tr`
  display: flex;
  height: 24px;
  border-collapse: collapse;
`;
const DetailTitle = styled.td<{ $fontWeight?: string }>`
  width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSize.FONT_2};
  font-weight: ${({ $fontWeight }) =>
    $fontWeight ? $fontWeight : theme.fontWeight.NORMAL};
  padding: 4px 8px;
  background-color: ${theme.color.neutral_50};
`;
const DetailText = styled.td<{ $fontWeight?: string }>`
  flex-grow: 1;
  font-size: ${theme.fontSize.FONT_2};
  font-weight: ${({ $fontWeight }) =>
    $fontWeight ? $fontWeight : theme.fontWeight.NORMAL};
  padding: 4px 8px;
  max-width: calc(100vw - 154px - 72px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconsWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.button<{
  $wrapperWH?: string;
  $iconWH?: string;
}>`
  width: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `28px`)};
  height: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `28px`)};
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ $iconWH }) => ($iconWH ? $iconWH : `14px`)};
    height: ${({ $iconWH }) => ($iconWH ? $iconWH : `14px`)};
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  color: ${theme.color.neutral_500};
  padding-top: 200px;
`;
